import { MathJaxObject } from "mathjax-full/js/components/startup";
import { ItemId } from "../../../model/datasource";
import { ContentItem } from "../../../model/datasource/item";
import { AnswerEvent, ToggleContextsEvent } from "../../../events";
import { MatchingAnswer } from "../../../lib/userdata";
import { DisplayItemFrontend } from "./displayitem";
import Sortable from "./sortable";

export class MatchingFrontend<I extends ItemId> extends DisplayItemFrontend<I> {
	displayItemContent(content: ContentItem<I>, bindTarget: EventTarget): void {
		const matching = document.querySelector('.item .matching');
		matching.classList.remove('d-none');
		matching.classList.add('invisible');

		const name = matching.querySelector('.name');
		const prompt = matching.querySelector('.prompt');
		const style = matching.querySelector('style');
		name.innerHTML = content.name + '.';
		prompt.innerHTML = content.matching.prompt;
		style.innerText = [].concat(
			content.matching.style,
			content.contexts.map(c => c.style),
		).join('\n\n');

		Array.from(matching.querySelectorAll('.context-panel')).slice(1)
			.forEach((panel: HTMLElement) => { panel.remove() });

		const contextPanel = matching.querySelector<HTMLElement>('.context-panel');
		const context = contextPanel.querySelector('.context');
		if (content.contexts.length > 0) {
			context.innerHTML = content.contexts[0].content_html;
			context.classList.remove('d-none');
			contextPanel.dataset.context = content.contexts[0].id;
			contextPanel.classList.remove('d-none');
			content.contexts.slice(1).reverse().forEach(_context => {
				const panel = contextPanel.cloneNode(true) as HTMLElement;
				const context = panel.querySelector('.context');
				context.innerHTML = _context.content_html;
				context.classList.remove('d-none');
				panel.dataset.context = _context.id;
				panel.classList.remove('d-none');
				panel.addEventListener('click', e => {
					bindTarget.dispatchEvent(new ToggleContextsEvent([panel.dataset.context]));
				});
				contextPanel.after(panel);
			});
		} else {
			context.innerHTML = '';
			context.classList.add('d-none');
			contextPanel.removeAttribute('data-context');
			contextPanel.classList.add('d-none');
		}

		const answers = matching.querySelector<HTMLElement>('.answers');
		const headers = Array.from(answers.querySelectorAll('.header'))
			.map(e => e.cloneNode(true));
		const columns = parseInt(answers.dataset.columns) || answers.children.length - headers.length;
		if (!answers.dataset.columns) {
			answers.dataset.columns = columns.toString();
		}
		const templates = Array.from(answers.children)
			.filter(a => !a.classList.contains('header'))
			.filter((a, index) => index % ((answers.children.length - headers.length) / columns) == 0)
			.map(e => e.cloneNode(true));
		answers.innerHTML = '';
		templates.forEach(element =>
			content.matching.answers.forEach((answer, index) => {
				const itemNode = element.cloneNode(true) as HTMLElement;
				itemNode.dataset.id = index.toString();
				answers.appendChild(itemNode);
			})
		);
		headers.forEach(header => answers.appendChild(header));
		answers.style.gridTemplateRows = `repeat(${content.matching.answers.length + (headers.length ? 1 : 0)}, min-content)`;

		const terms = answers.querySelectorAll('.term');
		const definitions = answers.querySelectorAll<HTMLElement>('.definition');

		content.matching.answers.forEach((answer, index) => {
			terms[index].innerHTML = answer.term;
			definitions[index].innerHTML = answer.definition;
		});

		new Sortable(answers, {
			draggable: '.sortable-term',
			disabled: true,
		}).sort(content.matching.answers.reduce(
			(res, answer, index) => {
				res[answer.term_order] = index;
				return res;
			}, []
		).filter(n => n !== undefined).map(String));
	}

	displayItemSelectedAnswer(content: ContentItem<I>, bindTarget: EventTarget, answer: MatchingAnswer | undefined): void {
		const matching = document.querySelector('.item .matching');

		const defListTarget = matching.querySelector<HTMLElement>('.answers');
		const indexToTermOrder = Object.fromEntries(content.matching.answers.map((key, index) => [index, key.term_order]));
		const termOrderToIndex = Object.fromEntries(content.matching.answers.map((key, index) => [key.term_order, index]));
		Sortable.get(defListTarget)?.destroy();
		const defList = new Sortable(defListTarget, {
			swap: true,
			draggable: '.sortable-definition',
			animation: 150,
			direction: 'vertical',
			onUpdate: e => {
				bindTarget.dispatchEvent(new AnswerEvent(
					defList.toArray().map(i => indexToTermOrder[parseInt(i)])
				));
				e.item.focus();
			},
			onKeyBindMove: e => {
				e.preventDefault();
				e.stopPropagation();
				bindTarget.dispatchEvent(new AnswerEvent(
					defList.toArray().map(i => indexToTermOrder[parseInt(i)])
				));
			}
		});
		defList.children().forEach(element => {
			element.tabIndex = 0;
		});
		defList.sort(answer && answer.length > 0
			? answer.map(n => termOrderToIndex[n]).filter(n => n !== undefined).map(String)
			: content.matching.answers.reduce(
				(res, answer, index) => {
					res[answer.definition_order] = index;
					return res;
				}, []
			).filter(n => n !== undefined).map(String)
		);
	}

	displayItemReview(content: ContentItem<I>, rawAnswer: MatchingAnswer | undefined): void {
		const matching = document.querySelector('.item .matching');
		const termOrderToIndex = Object.fromEntries(content.matching.answers.map((key, index) => [key.term_order, index]));
		const answer = rawAnswer?.map(n => termOrderToIndex[n]);

		const explanation = matching.querySelector('.card .explanation');
		const explanationPanel = explanation.closest('div.card');
		explanation.innerHTML = content.matching.explanation;

		const answerLength = rawAnswer?.length ?? 0;
		const isCorrect = Array.from(content.matching.answers)
			.map((answer, index) => ({ ...answer, index }))
			.sort((a, b) => a.term_order - b.term_order)
			.every((reference, index) => answer && reference.index === answer[index]);

		const correct = document.querySelector('.correct');

		if (answerLength > 0 && isCorrect) {
			correct.innerHTML = 'Correct';
			correct.classList.remove('alert-danger', 'alert-info');
			correct.classList.add('alert-success');
			explanationPanel.classList.remove('border-danger', 'border-info');
			explanationPanel.classList.add('border-success');
		} else if (answerLength > 0 && !isCorrect) {
			correct.innerHTML = 'Incorrect';
			correct.classList.remove('alert-success', 'alert-info');
			correct.classList.add('alert-danger');
			explanationPanel.classList.remove('border-success', 'border-info');
			explanationPanel.classList.add('border-danger');
		} else {
			correct.innerHTML = 'Unanswered';
			correct.classList.remove('alert-success', 'alert-danger');
			correct.classList.add('alert-info');
			explanationPanel.classList.remove('border-success', 'border-danger');
			explanationPanel.classList.add('border-info');
		}

		Sortable.get(matching.querySelector<HTMLElement>('.answers'))
			.option('disabled', true);

		const answers = matching.querySelectorAll<HTMLElement>('.answers .definition');
		const badges = matching.querySelectorAll<HTMLElement>('.answers .answer-correct');
		const references = matching.querySelectorAll<HTMLElement>('.answers .answer-reference');
		const explanations = matching.querySelectorAll<HTMLElement>('.answers .answer-explanation');

		Array.from(content.matching.answers)
			.map((answer, index) => ({ ...answer, index }))
			.sort((a, b) => a.term_order - b.term_order)
			.forEach((answerRef, index) => {
				const answerNode = answers[index];
				const reference = references[index];
				const correct = badges[index];
				const explanation = explanations[index];

				const isCorrect = answer?.[index] == answerRef.index;

				if (isCorrect) {
					answerNode.classList.remove('border-danger', 'border-info');
					answerNode.classList.add('border-success');
					correct.classList.remove('text-bg-danger', 'badge-danger', 'text-bg-info', 'badge-info');
					correct.classList.add('text-bg-success', 'badge-success');
					correct.innerHTML = 'Correct';
					reference.innerHTML = '';
					explanation.innerHTML = '';
				} else if (answerLength > 0 && !isCorrect) {
					answerNode.classList.remove('border-success', 'border-info');
					answerNode.classList.add('border-danger');
					correct.classList.remove('text-bg-success', 'badge-success', 'text-bg-info', 'badge-info');
					correct.classList.add('text-bg-danger', 'badge-danger');
					correct.innerHTML = `Incorrect`;
					reference.innerHTML = answerRef.definition;
					explanation.innerHTML = answerRef.explanation;
				} else {
					answerNode.classList.remove('border-success', 'border-danger');
					answerNode.classList.add('border-info');
					correct.classList.remove('text-bg-success', 'badge-success', 'text-bg-danger', 'badge-danger');
					correct.classList.add('text-bg-info', 'badge-info');
					correct.innerHTML = `Unanswered`;
					reference.innerHTML = answerRef.definition;
					explanation.innerHTML = answerRef.explanation;
				}
			});
	}

	displayItemRendered(content: ContentItem<I>, mathjax: MathJaxObject): void {
		const matching = document.querySelector('.item .matching');
		mathjax.typesetPromise().then(() => {
			matching.classList.remove('invisible');
		});
	}

	displayItemAlerts(content: ContentItem<I>): void {
		// Do nothing.
		return;
	}
}